// Shared IST date/time helpers — ALWAYS render/compute in Asia/Kolkata,
// regardless of the user's browser timezone (some devices are in UTC, which
// caused chat messages, standup times and check-in times to display wrong
// values). Use these helpers EVERYWHERE the app shows a time or date.

const IST_TZ = 'Asia/Kolkata';

// Backend sometimes returns "2026-06-01T08:01:22" with no timezone marker
// (MongoDB strips tzinfo from datetimes). JavaScript would parse this as the
// browser's LOCAL time — wrong, since the backend stores UTC. Normalise by
// appending `Z` whenever an offset isn't already present.
const _toUtcIso = (iso) => {
  if (!iso || typeof iso !== 'string') return iso;
  // Match `Z`, `+HH:MM`, or `-HH:MM` at the end (allowing optional fractional seconds)
  if (/Z$/.test(iso) || /[+-]\d{2}:?\d{2}$/.test(iso)) return iso;
  return iso + 'Z';
};

export const fmtIstTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(_toUtcIso(iso));
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('en-IN', {
      timeZone: IST_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

export const fmtIstDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(_toUtcIso(iso));
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-IN', {
      timeZone: IST_TZ, day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

export const fmtIstDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(_toUtcIso(iso)).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: IST_TZ,
    });
  } catch { return ''; }
};

// Smart chat-style formatter: "10:42" today, "Yesterday 10:42",
// "12 May 10:42" for older messages. All in IST.
export const fmtIstChatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(_toUtcIso(iso));
    if (Number.isNaN(d.getTime())) return '';
    const hm = d.toLocaleTimeString('en-IN', {
      timeZone: IST_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const istNow = new Date(new Date().toLocaleString('en-US', { timeZone: IST_TZ }));
    const istThen = new Date(d.toLocaleString('en-US', { timeZone: IST_TZ }));
    const today = new Date(istNow); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const tDay = new Date(istThen); tDay.setHours(0, 0, 0, 0);
    if (tDay.getTime() === today.getTime()) return hm;
    if (tDay.getTime() === yesterday.getTime()) return `Yesterday ${hm}`;
    return d.toLocaleDateString('en-IN', {
      timeZone: IST_TZ, day: '2-digit', month: 'short',
    }) + ` ${hm}`;
  } catch { return ''; }
};

// Returns today's date string (YYYY-MM-DD) AS IT IS IN ASIA/KOLKATA right now,
// regardless of the user's browser timezone. Critical for daily-check-in which
// would otherwise show "yesterday" for late-night IST users on browsers in UTC.
export const todayIst = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const d = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${d}`;
};
